import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { MAX_TAGS, NICHES } from "@/data/niches";

const AREA_LABELS = {
    lanes: "Lanes",
    topics: "Topics",
    summary: "Summary",
    other: "Something else",
};

const fmt = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso || "";
    }
};

/**
 * Lanes and topics for one creator, plus any "This is not me" flags the
 * creator sent. Creators cannot change lanes themselves; admins fix them
 * here. Tracked search questions are edited separately and never
 * regenerated, so the creator's history stays on one set.
 */
export default function CreatorLanesPanel({ creator, onSaved }) {
    const { request } = useApi();
    const [flags, setFlags] = useState([]);
    const [niches, setNiches] = useState(creator.niches || []);
    const [primary, setPrimary] = useState(creator.primary_niche || "");
    const [tagsText, setTagsText] = useState((creator.tags || []).join(", "));
    const [saving, setSaving] = useState(false);

    const loadFlags = useCallback(async () => {
        try {
            const res = await request(`/admin/creators/${creator.id}/flags`);
            setFlags(res?.items || []);
        } catch {
            setFlags([]);
        }
    }, [request, creator.id]);

    useEffect(() => {
        loadFlags();
    }, [loadFlags]);

    const openFlags = flags.filter((f) => f.status === "open");

    const toggle = (n) => {
        if (niches.includes(n)) {
            const next = niches.filter((x) => x !== n);
            setNiches(next);
            if (primary === n) setPrimary(next[0] || "");
        } else if (niches.length < 3) {
            setNiches([...niches, n]);
            if (!primary) setPrimary(n);
        } else {
            toast("Up to 3 lanes.");
        }
    };

    const save = async () => {
        const tags = tagsText
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .slice(0, MAX_TAGS);
        setSaving(true);
        try {
            await request(`/admin/creators/${creator.id}/lanes`, {
                method: "PATCH",
                body: {
                    primary_niche: primary,
                    niches,
                    tags,
                },
            });
            toast.success("Saved. Edit the tracked search questions to match if needed.");
            onSaved?.();
        } catch (err) {
            toast.error(err?.message || "Could not save lanes.");
        } finally {
            setSaving(false);
        }
    };

    const resolve = async (id) => {
        try {
            await request(`/admin/flags/${id}/resolve`, { method: "POST" });
            await loadFlags();
        } catch (err) {
            toast.error(err?.message || "Could not resolve.");
        }
    };

    return (
        <section
            data-testid="creator-lanes-panel"
            className="mb-4 bg-white border border-black/10 rounded-2xl px-5 py-4 space-y-4"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="samoony-label">Lanes and topics</div>
                {openFlags.length > 0 && (
                    <span
                        data-testid="creator-lanes-open-flags"
                        className="font-mono text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-signal-tint text-signal-text"
                    >
                        {openFlags.length} "not me"
                    </span>
                )}
            </div>

            {openFlags.map((f) => (
                <div
                    key={f.id}
                    data-testid={`creator-flag-${f.id}`}
                    className="rounded-xl border border-black/10 bg-signal-tint p-3 text-sm"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold">
                            This is not me: {AREA_LABELS[f.area] || f.area}
                        </div>
                        <button
                            type="button"
                            onClick={() => resolve(f.id)}
                            className="samoony-chip"
                        >
                            Resolve
                        </button>
                    </div>
                    <div className="text-xs text-black/50 mt-1">{fmt(f.created_at)}</div>
                    {f.note && <div className="mt-2">{f.note}</div>}
                    <div className="mt-2 text-xs text-black/60">
                        Shown: {(f.shown?.niches || []).join(", ") || "no lanes"}
                        {(f.shown?.tags || []).length > 0 && ` · ${f.shown.tags.join(", ")}`}
                    </div>
                    {f.shown?.summary && (
                        <div className="mt-1 text-xs text-black/60">"{f.shown.summary}"</div>
                    )}
                </div>
            ))}

            <div>
                <div className="text-xs text-black/60 mb-2">
                    Up to 3 lanes. Tap a selected lane to make it primary, tap the primary to remove it.
                </div>
                <div className="flex flex-wrap gap-2">
                    {NICHES.map((n) => {
                        const selected = niches.includes(n);
                        const isPrimary = primary === n;
                        return (
                            <button
                                key={n}
                                type="button"
                                data-selected={selected}
                                data-primary={isPrimary}
                                onClick={() =>
                                    selected && !isPrimary ? setPrimary(n) : toggle(n)
                                }
                                className="samoony-chip"
                            >
                                {n}
                                {isPrimary && (
                                    <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">
                                        primary
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <label className="block">
                <div className="text-xs text-black/60 mb-1">Topics, comma separated</div>
                <input
                    data-testid="creator-lanes-tags"
                    className="samoony-input w-full"
                    value={tagsText}
                    onChange={(e) => setTagsText(e.target.value)}
                />
            </label>

            <button
                type="button"
                data-testid="creator-lanes-save"
                onClick={save}
                disabled={saving || !primary || niches.length === 0}
                className="samoony-pill samoony-pill-primary disabled:opacity-30"
            >
                {saving ? "Saving..." : "Save lanes and topics"}
            </button>
        </section>
    );
}
